import { useEffect, useMemo, useState } from 'react';

import { Field, Empty } from '../../components/ui/bits.jsx';
import { useUI } from '../../components/ui/feedback.jsx';
import { Segments } from '../../components/AppHeader.jsx';
import { Icon } from '../../components/ui/Icon.jsx';
import * as api from '../../lib/api.js';
import { fmtDate } from '../../lib/format.js';
import { useSola, solaNastavitve } from '../../sola.jsx';
import { POGOJI_PRIVZETO, validateProgramChange, defaultRows, SKLOPI_PRIVZETO, slug, parseProgramText, sklopiIzBesedila } from '../../lib/program.js';
import { PLANI, ZA_SOLE_URL } from '../../config.js';
import { t } from '../../i18n/index.js';

/*
  Šola: kar inštruktor ureja sam.

    Nastavitve   predstavitev, logotip, pogoji za izpit, prag testa, agencija
    Program      vaje in pod-vaje (preimenuj, dodaj, skrij — nikoli briši)
    Inštruktorji kdo še vidi tečajnike (po e-pošti)
    Licenca      paket, veljavnost, koda in povezava za vpis — samo branje

  Plana, statusa in veljavnosti šola ne spreminja; to naredi platforma
  (varuje trigger sole_guard v bazi, ne ta zaslon).
*/

export function Sola({ users, onSolaChanged, onProgramChanged }) {
  const { vloga } = useSola();
  const [seg, setSeg] = useState('nastavitve');

  return (
    <div className="stack">
      <Segments
        label={t('Šola')}
        value={seg}
        onChange={setSeg}
        items={[
          { id: 'nastavitve', icon: 'settings', label: t('Nastavitve') },
          { id: 'program', icon: 'list', label: t('Program') },
          { id: 'instruktorji', icon: 'users', label: t('Inštruktorji') },
          { id: 'licenca', icon: 'key', label: t('Licenca') },
        ]}
      />
      {seg === 'nastavitve' && <Nastavitve onSaved={onSolaChanged} />}
      {seg === 'program' && <ProgramEditor users={users} onSaved={onProgramChanged} />}
      {seg === 'instruktorji' && <Instruktorji canEdit={vloga !== 'instruktor'} />}
      {seg === 'licenca' && <Licenca users={users} />}
    </div>
  );
}

/* ── Nastavitve ─────────────────────────────────────────────── */

function Nastavitve({ onSaved }) {
  const { toast } = useUI();
  const { sola } = useSola();
  const n = useMemo(() => solaNastavitve(sola), [sola]);
  const [form, setForm] = useState(() => ({
    ime: sola.ime ?? '',
    kratko: sola.kratko ?? '',
    email: sola.email ?? '',
    telefon: sola.telefon ?? '',
    naslov: sola.naslov ?? '',
    splet: sola.splet ?? '',
    jezik: sola.jezik ?? 'sl',
    izpit: { ...POGOJI_PRIVZETO, ...(sola.nastavitve?.izpit ?? {}) },
    prag: n.prag,
    povezave: { ...n.povezave },
    agencija: { ...n.agencija },
  }));
  const [saving, setSaving] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setIn = (k, patch) => setForm((f) => ({ ...f, [k]: { ...f[k], ...patch } }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.updateSola(sola.id, {
        ime: form.ime.trim(),
        kratko: form.kratko.trim() || null,
        email: form.email.trim() || null,
        telefon: form.telefon.trim() || null,
        naslov: form.naslov.trim() || null,
        splet: form.splet.trim() || null,
        jezik: form.jezik,
        nastavitve: {
          ...(sola.nastavitve ?? {}),
          izpit: Object.fromEntries(Object.entries(form.izpit).map(([k, v]) => [k, Number(v) || 0])),
          prag: Math.min(100, Math.max(1, Number(form.prag) || 80)),
          povezave: form.povezave,
          agencija: form.agencija,
        },
      });
      toast.ok(t('Nastavitve šole so shranjene.'));
      await onSaved?.();
    } catch (err) {
      toast.bad(err.message ?? t('Shranjevanje ni uspelo.'));
    } finally {
      setSaving(false);
    }
  };

  const pickLogo = async (file) => {
    if (!file) return;
    setLogoBusy(true);
    try {
      await api.uploadSolaLogo(sola.id, file);
      toast.ok(t('Logotip je naložen.'));
      await onSaved?.();
    } catch (err) {
      toast.bad(err.message ?? t('Nalaganje ni uspelo.'));
    } finally {
      setLogoBusy(false);
    }
  };

  return (
    <form className="stack" onSubmit={save}>
      <section className="card">
        <div className="card__head">
          <span className="card__title">{t('Predstavitev')}</span>
        </div>
        <div className="card__body stack">
          <div className="row row--top" style={{ gap: 'var(--s-4)' }}>
            <div
              className="card"
              style={{ width: 120, height: 120, display: 'grid', placeItems: 'center', padding: 'var(--s-2)' }}
            >
              {sola.logo_url ? (
                <img src={sola.logo_url} alt="" style={{ maxWidth: '100%', maxHeight: '100%' }} />
              ) : (
                <Icon name="school" size={40} className="t-dimmer" />
              )}
            </div>
            <div className="stack stack--tight">
              <span className="t-md t-bold">{t('Logotip')}</span>
              <span className="t-sm t-dim">{t('PNG ali SVG s prozornim ozadjem, največ 2 MB. Prikaže se v glavi aplikacije in na prijavi.')}</span>
              <label className="btn btn--secondary btn--sm" style={{ alignSelf: 'flex-start' }}>
                <Icon name="upload" size={15} /> {logoBusy ? t('Nalagam …') : t('Naloži logotip')}
                <input type="file" accept="image/*" hidden disabled={logoBusy} onChange={(e) => pickLogo(e.target.files?.[0])} />
              </label>
            </div>
          </div>

          <div className="grid-2">
            <Field label={t('Ime šole')} id="s-ime" required>
              <input id="s-ime" className="input" value={form.ime} onChange={(e) => set({ ime: e.target.value })} />
            </Field>
            <Field label={t('Kratko ime (v glavi)')} id="s-kratko" hint={t('npr. Učilnica')}>
              <input id="s-kratko" className="input" value={form.kratko} onChange={(e) => set({ kratko: e.target.value })} />
            </Field>
            <Field label={t('E-pošta')} id="s-email">
              <input id="s-email" className="input" type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} />
            </Field>
            <Field label={t('Telefon')} id="s-tel">
              <input id="s-tel" className="input" value={form.telefon} onChange={(e) => set({ telefon: e.target.value })} />
            </Field>
            <Field label={t('Naslov')} id="s-naslov">
              <input id="s-naslov" className="input" value={form.naslov} onChange={(e) => set({ naslov: e.target.value })} />
            </Field>
            <Field label={t('Spletna stran')} id="s-splet">
              <input id="s-splet" className="input" value={form.splet} onChange={(e) => set({ splet: e.target.value })} />
            </Field>
            <Field label={t('Jezik vmesnika')} id="s-jezik" hint={t('Vsebine (razlage, test) pišeš v svojem jeziku ne glede na to.')}>
              <select id="s-jezik" className="input" value={form.jezik} onChange={(e) => set({ jezik: e.target.value })}>
                <option value="sl">Slovenščina</option>
                <option value="en">English</option>
                <option value="hr">{t('Hrvatski (vmesnik v slovenščini)')}</option>
                <option value="de">{t('Deutsch (vmesnik v angleščini)')}</option>
                <option value="it">{t('Italiano (vmesnik v angleščini)')}</option>
              </select>
            </Field>
          </div>
        </div>
      </section>

      <section className="card">
        <div className="card__head">
          <span className="card__title">{t('Pogoji za izpit')}</span>
        </div>
        <div className="card__body stack">
          <p className="t-sm t-dim">
            {t('Po teh številkah aplikacija računa „Pot do izpita“. Vpiši, kar zahteva tvoja agencija ali zveza.')}
          </p>
          <div className="grid-3">
            <Num label={t('Najmanj potrjenih letov')} k="min_letov" form={form} setIn={setIn} />
            <Num label={t('Najmanj različnih vzletišč')} k="min_vzletisc" form={form} setIn={setIn} />
            <Num label={t('Najnižja starost (let)')} k="min_starost" form={form} setIn={setIn} />
            <Num label={t('Veljavnost zdravniškega (mesecev)')} k="zdravniski_meseci" form={form} setIn={setIn} />
            <Num label={t('Višinska razlika leta (m)')} k="visinska_razlika" form={form} setIn={setIn} hint={t('Samo za prikaz; aplikacija je ne preverja.')} />
            <Field label={t('Prag zaključnega testa (%)')} id="s-prag">
              <input id="s-prag" type="number" min="1" max="100" className="input" value={form.prag} onChange={(e) => set({ prag: e.target.value })} />
            </Field>
          </div>
        </div>
      </section>

      <section className="card">
        <div className="card__head">
          <span className="card__title">{t('Agencija in prijava na izpit')}</span>
        </div>
        <div className="card__body stack">
          <p className="t-sm t-dim">{t('Prikaže se tečajniku v zavihku Izpit. Prazna polja se ne prikažejo.')}</p>
          <div className="grid-2">
            <Field label={t('Naziv agencije')} id="a-naziv">
              <input id="a-naziv" className="input" value={form.agencija.naziv ?? ''} onChange={(e) => setIn('agencija', { naziv: e.target.value })} />
            </Field>
            <Field label={t('Spletna stran agencije')} id="a-url">
              <input id="a-url" className="input" value={form.agencija.url ?? ''} onChange={(e) => setIn('agencija', { url: e.target.value })} />
            </Field>
            <Field label={t('Ime prijavnice')} id="a-pime">
              <input id="a-pime" className="input" value={form.agencija.prijavnica_ime ?? ''} onChange={(e) => setIn('agencija', { prijavnica_ime: e.target.value })} />
            </Field>
            <Field label={t('Povezava do prijavnice')} id="a-purl">
              <input id="a-purl" className="input" value={form.agencija.prijavnica_url ?? ''} onChange={(e) => setIn('agencija', { prijavnica_url: e.target.value })} />
            </Field>
            <Field label={t('Izpitni roki (povezava)')} id="a-roki">
              <input id="a-roki" className="input" value={form.agencija.roki_url ?? ''} onChange={(e) => setIn('agencija', { roki_url: e.target.value })} />
            </Field>
            <Field label={t('Navodila šole za prijavo (povezava)')} id="a-blog">
              <input id="a-blog" className="input" value={form.agencija.blog_url ?? ''} onChange={(e) => setIn('agencija', { blog_url: e.target.value })} />
            </Field>
          </div>
          <Field label={t('Naslov za pošiljanje prijave')} id="a-naslov" hint={t('Več vrstic.')}>
            <textarea id="a-naslov" className="input" rows={3} value={form.agencija.naslov ?? ''} onChange={(e) => setIn('agencija', { naslov: e.target.value })} />
          </Field>
          <div className="grid-2">
            <Field label={t('Teoretična vprašanja (povezava)')} id="p-vpr" hint={t('Hitra povezava na zaslonu tečajnika.')}>
              <input id="p-vpr" className="input" value={form.povezave.vprasanja ?? ''} onChange={(e) => setIn('povezave', { vprasanja: e.target.value })} />
            </Field>
            <Field label={t('Učbenik (povezava)')} id="p-ucb">
              <input id="p-ucb" className="input" value={form.povezave.ucbenik ?? ''} onChange={(e) => setIn('povezave', { ucbenik: e.target.value })} />
            </Field>
          </div>
        </div>
      </section>

      <div className="sticky-bar">
        <span className="spacer t-sm t-dim">{t('Spremembe veljajo za vse tečajnike šole.')}</span>
        <button className="btn btn--primary" type="submit" disabled={saving}>
          {saving ? t('Shranjujem …') : t('Shrani nastavitve')}
        </button>
      </div>
    </form>
  );
}

function Num({ label, k, form, setIn, hint }) {
  return (
    <Field label={label} id={`n-${k}`} hint={hint}>
      <input
        id={`n-${k}`}
        type="number"
        min="0"
        className="input"
        value={form.izpit[k] ?? ''}
        onChange={(e) => setIn('izpit', { [k]: e.target.value })}
      />
    </Field>
  );
}

/* ── Program ─────────────────────────────────────────────────
   Pod-vaje so ključi v tabeli progress: vrstni red in število se ne
   smeta zmanjšati. Zato: preimenuj, dodaj na konec, skrij. Brisanja ni. */

function ProgramEditor({ users, onSaved }) {
  const { toast, confirm } = useUI();
  const { sola, program, programRows } = useSola();
  const [rows, setRows] = useState(() => program.rows.map((r) => ({ ...r, subs: [...(r.subs ?? [])] })));
  const [labels, setLabels] = useState(() => ({ ...SKLOPI_PRIVZETO, ...(sola.nastavitve?.sklopi ?? {}) }));
  const [saving, setSaving] = useState(false);
  const [besedilo, setBesedilo] = useState('');
  const jePrivzet = program.rows.length === 0 || !sola.nastavitve?.program_lasten;
  const aktivnih = users.filter((u) => !u.testni && !u.archived && u.ime).length;

  /*
    Uvoz iz besedila: šola prilepi svoj program (vrstica = vaja, vezaj =
    pod-vaja, # = sklop). Brez tečajnikov lahko besedilo program v celoti
    zamenja; s tečajniki se vaje samo dodajo (obstoječe pod-vaje so ključi
    v progress in se ne smejo premakniti).
  */
  const uvozi = () => {
    const nove = parseProgramText(besedilo);
    if (!nove.length) {
      toast.bad(t('Besedilo ne vsebuje nobene vaje.'));
      return;
    }
    const oznake = sklopiIzBesedila(besedilo);
    if (aktivnih === 0) {
      setRows(nove);
      setLabels({ ...SKLOPI_PRIVZETO, ...oznake });
      toast.ok(t('Prebranih {n} vaj. Preglej in shrani.', { n: nove.length }));
    } else {
      const obstoj = new Set(rows.map((r) => r.id));
      let n = rows.length;
      const dodane = nove.map((r) => {
        let id = r.id;
        while (obstoj.has(id)) id = `v${++n}`;
        obstoj.add(id);
        return { ...r, id, sklop_ord: r.sklop_ord + sklopi.length };
      });
      setRows((l) => [...l, ...dodane]);
      setLabels((x) => ({ ...x, ...oznake }));
      toast.ok(t('Dodanih {n} vaj na konec. Preglej in shrani.', { n: dodane.length }));
    }
    setBesedilo('');
  };

  const sklopi = useMemo(() => {
    const m = new Map();
    rows
      .slice()
      .sort((a, b) => a.sklop_ord - b.sklop_ord || a.ord - b.ord)
      .forEach((r) => {
        if (!m.has(r.sklop)) m.set(r.sklop, { key: r.sklop, ord: r.sklop_ord, list: [] });
        m.get(r.sklop).list.push(r);
      });
    return [...m.values()];
  }, [rows]);

  const upd = (id, patch) => setRows((l) => l.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const updSub = (id, i, v) =>
    setRows((l) => l.map((r) => (r.id === id ? { ...r, subs: r.subs.map((s, j) => (j === i ? v : s)) } : r)));
  const addSub = (id) => setRows((l) => l.map((r) => (r.id === id ? { ...r, subs: [...r.subs, ''] } : r)));
  const addEx = (sklop, sklopOrd) => {
    const n = rows.filter((r) => r.sklop === sklop).length;
    let id = `${slug(sklop).slice(0, 3) || 'v'}${n + 1}`;
    while (rows.some((r) => r.id === id)) id += 'x';
    setRows((l) => [...l, { id, sklop, sklop_ord: sklopOrd, ord: n, title: `Vaja ${n + 1}: `, subs: [''], skrito: false }]);
  };
  const addSklop = () => {
    const key = `sklop${sklopi.length + 1}`;
    setLabels((x) => ({ ...x, [key]: `Nov sklop ${sklopi.length + 1}` }));
    setRows((l) => [...l, { id: `${key}-1`, sklop: key, sklop_ord: sklopi.length, ord: 0, title: 'Vaja 1: ', subs: [''], skrito: false }]);
  };

  const save = async () => {
    const clean = rows.map((r) => ({ ...r, title: r.title.trim(), subs: r.subs.map((s) => s.trim()).filter(Boolean) }));
    // Proti čemu preverjamo: proti shranjenim vrsticam; če jih ni in šola
    // nima tečajnikov, je program še prost in ga sme zamenjati v celoti.
    const shranjeni = programRows.length || aktivnih > 0 ? program.rows : [];
    const errs = validateProgramChange(shranjeni, clean);
    if (errs.length) {
      toast.bad(errs[0], { duration: 9000 });
      return;
    }
    const n = users.filter((u) => !u.testni && !u.archived).length;
    if (n > 0) {
      const ok = await confirm({
        title: t('Shranim program?'),
        body: t('Velja za vseh {n} aktivnih tečajnikov. Že potrjene vaje ostanejo potrjene.', { n }),
        confirmLabel: t('Shrani'),
      });
      if (!ok) return;
    }
    setSaving(true);
    try {
      await api.saveProgram(sola.id, clean);
      await api.updateSola(sola.id, { nastavitve: { ...(sola.nastavitve ?? {}), sklopi: labels, program_lasten: true } });
      toast.ok(t('Program je shranjen.'));
      await onSaved?.();
    } catch (e) {
      toast.bad(e.message ?? t('Shranjevanje ni uspelo.'));
    } finally {
      setSaving(false);
    }
  };

  const prepisiPerut = () => {
    setRows(defaultRows());
    setLabels({ ...SKLOPI_PRIVZETO });
  };

  return (
    <div className="stack">
      <section className="card card--brand card--pad stack stack--tight">
        <h2 className="t-lg">{t('Program šolanja')}</h2>
        <p className="t-sm t-dim">
          {t('Vaje in pod-vaje, ki jih tečajniki odkljukajo in ti potrdiš. Pod-vaj ne moreš izbrisati ali preurediti — nanje so vezane že potrjene vaje — lahko pa jih preimenuješ, dodaš nove na konec ali skriješ celo vajo.')}
        </p>
        {jePrivzet && rows.length === 0 && (
          <button className="btn btn--secondary btn--sm" style={{ alignSelf: 'flex-start' }} onClick={prepisiPerut}>
            {t('Začni s Perutovim programom')}
          </button>
        )}
      </section>

      <details className="card card--pad">
        <summary className="t-md t-bold" style={{ cursor: 'pointer' }}>
          {t('Uvozi program iz besedila')}
        </summary>
        <div className="stack" style={{ marginTop: 'var(--s-3)' }}>
          <p className="t-sm t-dim">
            {t('Prilepi program iz svojega dokumenta: vrstica je vaja, vrstica z vezajem je pod-vaja, vrstica z # je sklop.')}{' '}
            {aktivnih === 0
              ? t('Ker šola še nima tečajnikov, besedilo zamenja celoten program.')
              : t('Šola ima {n} tečajnikov, zato se vaje iz besedila dodajo na konec; obstoječih ne spreminja.', { n: aktivnih })}
          </p>
          <textarea
            className="input"
            rows={8}
            value={besedilo}
            aria-label={t('Program kot besedilo')}
            placeholder={t('# Začetni tečaj\nVaja 1: Priprava\n- Razprostiranje krila\n- Pet kontrolnih točk\nVaja 2: Dvig krila\n- Polnjenje')}
            style={{ fontFamily: 'var(--font-num)', fontSize: 'var(--fs-sm)' }}
            onChange={(e) => setBesedilo(e.target.value)}
          />
          <button className="btn btn--secondary" style={{ alignSelf: 'flex-start' }} onClick={uvozi} disabled={!besedilo.trim()}>
            <Icon name="upload" size={15} /> {t('Preberi besedilo')}
          </button>
        </div>
      </details>

      {sklopi.map((s) => (
        <section className="card" key={s.key}>
          <div className="card__head">
            <input
              className="input"
              style={{ minHeight: 36, fontWeight: 800, maxWidth: 320 }}
              value={labels[s.key] ?? s.key}
              aria-label={t('Ime sklopa')}
              onChange={(e) => setLabels((x) => ({ ...x, [s.key]: e.target.value }))}
            />
            <span className="spacer" />
            <span className="t-xs t-dimmer num">{s.list.length} {t('vaj')}</span>
          </div>
          <div className="stack prog-list">
            {s.list.map((r) => (
              <div key={r.id} className="card card--pad stack stack--tight" style={{ opacity: r.skrito ? 0.55 : 1 }}>
                <div className="row row--wrap">
                  <span className="t-xs t-dimmer num" style={{ minWidth: 34 }}>
                    {r.id}
                  </span>
                  <input className="input" style={{ flex: '1 1 220px', minWidth: 0 }} value={r.title} aria-label={t('Naslov vaje {id}', { id: r.id })} onChange={(e) => upd(r.id, { title: e.target.value })} />
                  <label className="row t-xs t-dim" style={{ gap: 'var(--s-1)', cursor: 'pointer' }}>
                    <input type="checkbox" className="checkbox" checked={!!r.skrito} onChange={(e) => upd(r.id, { skrito: e.target.checked })} />
                    {t('skrita')}
                  </label>
                </div>
                <ol className="stack stack--tight" style={{ paddingLeft: 'var(--s-6)' }}>
                  {r.subs.map((sub, i) => (
                    <li key={i}>
                      <input
                        className="input"
                        style={{ minHeight: 36 }}
                        value={sub}
                        aria-label={t('Pod-vaja {n} vaje {id}', { n: i + 1, id: r.id })}
                        onChange={(e) => updSub(r.id, i, e.target.value)}
                      />
                    </li>
                  ))}
                </ol>
                <button className="btn btn--sm btn--ghost" style={{ alignSelf: 'flex-start' }} onClick={() => addSub(r.id)}>
                  <Icon name="plus" size={14} /> {t('Pod-vaja')}
                </button>
              </div>
            ))}
            <button className="btn btn--sm btn--secondary" style={{ alignSelf: 'flex-start' }} onClick={() => addEx(s.key, s.ord)}>
              <Icon name="plus" size={14} /> {t('Nova vaja v ta sklop')}
            </button>
          </div>
        </section>
      ))}

      <button className="btn btn--sm btn--ghost" style={{ alignSelf: 'flex-start' }} onClick={addSklop}>
        <Icon name="plus" size={14} /> {t('Nov sklop (npr. tandem, SIV)')}
      </button>

      <div className="sticky-bar">
        <span className="spacer t-sm t-dim">
          {rows.length} {t('vaj')} · {rows.reduce((a, r) => a + r.subs.filter((x) => x.trim()).length, 0)} {t('pod-vaj')}
        </span>
        <button className="btn btn--primary" onClick={save} disabled={saving || !rows.length}>
          {saving ? t('Shranjujem …') : t('Shrani program')}
        </button>
      </div>
    </div>
  );
}

/* ── Inštruktorji ───────────────────────────────────────────── */

function Instruktorji({ canEdit }) {
  const { toast, confirm } = useUI();
  const { sola } = useSola();
  const [list, setList] = useState(null);
  const [email, setEmail] = useState('');
  const [ime, setIme] = useState('');
  const [busy, setBusy] = useState(false);

  const reload = () => api.loadClani(sola.id).then(setList).catch((e) => toast.bad(e.message));
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sola.id]);

  const add = async (e) => {
    e.preventDefault();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      toast.bad(t('Vpiši veljaven e-poštni naslov.'));
      return;
    }
    setBusy(true);
    try {
      await api.addClan(sola.id, email, 'instruktor', ime);
      toast.ok(t('Inštruktor je dodan. Prijavi se z Googlom s tem naslovom.'));
      setEmail('');
      setIme('');
      await reload();
    } catch (err) {
      toast.bad(err.message ?? t('Ni uspelo.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    const ok = await confirm({
      title: t('Odstranim inštruktorja?'),
      body: t('{who} ne bo več videl tečajnikov te šole.', { who: c.ime ?? c.email }),
      confirmLabel: t('Odstrani'),
      danger: true,
    });
    if (!ok) return;
    try {
      await api.removeClan(c.id);
      toast.ok(t('Odstranjen.'));
      await reload();
    } catch (err) {
      toast.bad(err.message ?? t('Ni uspelo.'));
    }
  };

  return (
    <div className="stack">
      <section className="card card--brand card--pad stack stack--tight">
        <h2 className="t-lg">{t('Inštruktorji')}</h2>
        <p className="t-sm t-dim">
          {t('Vsak inštruktor se prijavi s svojim Google računom. Dodaš ga po e-pošti; vidi in potrjuje tečajnike te šole, ne vidi pa opomb platforme in ne ureja licence.')}
        </p>
      </section>

      {list === null ? (
        <p className="t-sm t-dim" role="status">{t('Nalagam …')}</p>
      ) : (
        <div className="card list">
          {list.map((c) => (
            <div key={c.id} className="list__row">
              <Icon name={c.vloga === 'lastnik' ? 'star' : 'users'} size={18} className={c.vloga === 'lastnik' ? 't-brand' : 't-dimmer'} />
              <span className="spacer">
                <span className="t-md t-bold" style={{ display: 'block' }}>{c.ime ?? c.email}</span>
                <span className="t-xs t-dim">{c.email} · {c.vloga === 'lastnik' ? t('lastnik') : t('inštruktor')}{c.user_id ? '' : t(' · še ni prijavljen')}</span>
              </span>
              {canEdit && c.vloga !== 'lastnik' && (
                <button className="btn-icon btn-icon--sm" onClick={() => remove(c)} aria-label={t('Odstrani {name}', { name: c.email })}>
                  <Icon name="x" size={15} />
                </button>
              )}
            </div>
          ))}
          {list.length === 0 && <Empty icon="users" title={t('Ni inštruktorjev')} />}
        </div>
      )}

      {canEdit && (
        <form className="card card--pad stack" onSubmit={add}>
          <h3 className="t-base">{t('Dodaj inštruktorja')}</h3>
          <div className="grid-2">
            <Field label={t('E-pošta (Google račun)')} id="i-email" required>
              <input id="i-email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </Field>
            <Field label={t('Ime')} id="i-ime">
              <input id="i-ime" className="input" value={ime} onChange={(e) => setIme(e.target.value)} />
            </Field>
          </div>
          <button className="btn btn--primary" type="submit" disabled={busy || !email} style={{ alignSelf: 'flex-start' }}>
            {busy ? t('Dodajam …') : t('Dodaj')}
          </button>
        </form>
      )}
    </div>
  );
}

/* ── Licenca ────────────────────────────────────────────────── */

function Licenca({ users }) {
  const { toast } = useUI();
  const { sola, platforma } = useSola();
  const plan = PLANI.find((p) => p.id === sola.plan) ?? PLANI[0];
  const aktivnih = users.filter((u) => !u.testni && !u.archived && u.ime).length;
  const max = sola.max_tecajnikov ?? plan.max;
  const povezava = `${window.location.origin}/?sola=${sola.slug}`;
  const dni = sola.velja_do ? Math.round((new Date(sola.velja_do) - new Date()) / 86400000) : null;

  const copy = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      toast.ok(t('Kopirano.'));
    } catch {
      toast.info(txt);
    }
  };

  const STATUS = { preizkus: [t('Preizkus'), 'wait'], aktivna: [t('Aktivna'), 'ok'], zamrznjena: [t('Zamrznjena'), 'bad'], zaprta: [t('Zaprta'), 'bad'] };
  const [stLabel, stTone] = STATUS[sola.status] ?? [sola.status, 'mute'];

  return (
    <div className="stack">
      <section className="card">
        <div className="card__head">
          <span className="card__title spacer">{t('Licenca')}</span>
          <span className={`badge badge--${stTone}`}>{stLabel}</span>
        </div>
        <div className="card__body">
          <dl className="kv">
            <dt>{t('Paket')}</dt>
            <dd className="t-bold">{plan.ime}{plan.cena ? ` · ${plan.cena} € / ${t('leto')}` : ''}</dd>
            <dt>{t('Velja do')}</dt>
            <dd>
              {sola.velja_do ? fmtDate(sola.velja_do) : t('brez omejitve')}
              {dni != null && dni >= 0 && dni <= 30 && <span className="badge badge--wait" style={{ marginLeft: 8 }}>{t('čez {n} dni', { n: dni })}</span>}
              {dni != null && dni < 0 && <span className="badge badge--bad" style={{ marginLeft: 8 }}>{t('potekla')}</span>}
            </dd>
            <dt>{t('Tečajniki')}</dt>
            <dd className="num">
              {aktivnih}{max ? ` / ${max}` : ''} {t('aktivnih')}
              {max && aktivnih >= max && <span className="badge badge--bad" style={{ marginLeft: 8 }}>{t('kvota polna')}</span>}
            </dd>
            <dt>{t('Oznaka šole')}</dt>
            <dd className="num">{sola.slug}</dd>
          </dl>
          {!platforma && (
            <p className="t-sm t-dim" style={{ marginTop: 'var(--s-3)' }}>
              {t('Za spremembo paketa ali podaljšanje piši na')}{' '}
              <a href="mailto:info@perut.net?subject=Učilnica – licenca">info@perut.net</a>. {t('Cenik in paketi:')}{' '}
              <a href={ZA_SOLE_URL} target="_blank" rel="noopener noreferrer">
                {window.location.origin}{ZA_SOLE_URL}
              </a>
              .
            </p>
          )}
        </div>
      </section>

      <section className="card">
        <div className="card__head">
          <span className="card__title">{t('Vpis tečajnikov')}</span>
        </div>
        <div className="card__body stack">
          <p className="t-sm t-dim">
            {t('Tečajnik se prijavi z Googlom in izbere tvojo šolo. Najlažje: pošlji mu to povezavo ali kodo šole. Oboje lahko objaviš na svoji spletni strani.')}
          </p>
          <div className="row row--wrap">
            <code className="card card--pad t-sm" style={{ padding: 'var(--s-2) var(--s-3)' }}>{povezava}</code>
            <button className="btn btn--sm btn--secondary" onClick={() => copy(povezava)}>{t('Kopiraj povezavo')}</button>
          </div>
          <div className="row row--wrap">
            <span className="t-sm t-dim">{t('Koda šole:')}</span>
            <strong className="t-lg num">{sola.koda ?? '—'}</strong>
            {sola.koda && <button className="btn btn--sm btn--ghost" onClick={() => copy(sola.koda)}>{t('Kopiraj')}</button>}
          </div>
        </div>
      </section>
    </div>
  );
}
